import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { nexsysApi } from "./api";
import { NexSysMark } from "./ui";
import { Sidebar, TopBar, AIPanel, CommandPalette, Notifications, MobileNav } from "./panels";
import { Overview, RiskView, TimelineView, DigitalTwinView, PredictiveView, GovernanceView, SignalsView } from "./views";
import { DecisionCenter } from "./DecisionCenter";
import { ExecutionConsole } from "./ExecutionConsole";
import { Integrations } from "./Integrations";
import { CognitiveCore } from "./CognitiveCore";
import { Continuum } from "./Continuum";
import { GatewayConsole } from "./GatewayConsole";
import { AccessLog } from "./AccessLog";
import { useChatty } from "../chatty/ChattyContext";

const SEED_NOTIFS = [
  { id: 1, title: "Scope creep detected", body: "ERP Modernization · integration phase (sample)", color: "#ff3b6b" },
  { id: 2, title: "Optimization available", body: "Reallocate 4 engineers to Data Fabric (sample)", color: "#22d3ee" },
];

const notifsFromLive = (ins) =>
  (ins.risks || []).slice(0, 2).map((r, i) => ({
    id: `live-${r.id || i}`,
    title: r.level === "Critical" ? "Critical risk open" : "High-priority risk open",
    body: `${r.title} · ${r.program}`.slice(0, 80),
    color: r.level === "Critical" ? "#ff3b6b" : "#ff9f43",
  }));

function Boot({ done }) {
  return (
    <AnimatePresence>
      {!done && (
        <motion.div
          initial={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.6 }}
          className="fixed inset-0 z-[120] flex flex-col items-center justify-center bg-[#05070f]"
          data-testid="boot-overlay"
        >
          <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 0.6 }}>
            <NexSysMark size={72} />
          </motion.div>
          <div className="mt-6 font-display text-2xl font-bold tracking-tight">
            <span className="text-white">Nex</span><span className="text-cyan-400">Sys</span>
          </div>
          <div className="mt-1 font-mono text-[10px] tracking-[0.3em] text-cyan-400/60">COMMAND CENTER</div>
          <div className="mt-8 h-[2px] w-64 overflow-hidden rounded-full bg-white/10">
            <div className="nx-boot-bar h-full bg-gradient-to-r from-cyan-400 to-indigo-500" />
          </div>
          <div className="mt-3 font-mono text-[10px] text-white/40">
            Initializing intelligence core<span className="nx-blink">_</span>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default function CommandCenter() {
  const chatty = useChatty();
  const [booted, setBooted] = useState(false);
  const [view, setView] = useState("command");
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [navOpen, setNavOpen] = useState(false);
  const [notifs, setNotifs] = useState(SEED_NOTIFS);
  const liveNotifsSet = useRef(false);

  const [portfolio, setPortfolio] = useState(null);
  const [insights, setInsights] = useState(null);
  const [timeseries, setTimeseries] = useState([]);
  const [tsMeta, setTsMeta] = useState(null);

  useEffect(() => {
    document.title = "NexSys Command Center — Nexis.team";
    const t = setTimeout(() => setBooted(true), 1900);
    return () => clearTimeout(t);
  }, []);

  // expose the active view + a setter so Chatty can read and drive navigation
  useEffect(() => {
    window.__nexsysView = view;
    return () => { window.__nexsysView = ""; };
  }, [view]);

  useEffect(() => {
    window.__nexsysSetView = (id) => setView(id);
    return () => { delete window.__nexsysSetView; };
  }, []);

  const loadLive = useCallback(async () => {
    try {
      const [p, ins] = await Promise.all([nexsysApi.portfolio(), nexsysApi.insights()]);
      setPortfolio(p);
      setInsights(ins);
      if (ins?.risks_source && !liveNotifsSet.current) {
        liveNotifsSet.current = true;
        setNotifs(notifsFromLive(ins));
      }
    } catch (e) { /* backend offline: views degrade gracefully */ }
  }, []);

  useEffect(() => {
    loadLive();
    nexsysApi.timeseries().then((d) => {
      setTimeseries(d.series || []);
      setTsMeta({ source: d.source, history: !!d.history, points: d.points });
    }).catch(() => {});
    const poll = setInterval(loadLive, 15000);
    return () => clearInterval(poll);
  }, [loadLive]);

  // Cmd/Ctrl+K
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") { e.preventDefault(); setPaletteOpen((o) => !o); }
      if (e.key === "Escape") setPaletteOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const runCommand = useCallback((msg) => {
    chatty?.sendGrounded(msg, () => nexsysApi.analyze(msg));
  }, [chatty]);
  const dismiss = (id) => setNotifs((n) => n.filter((x) => x.id !== id));

  // Floating notifications behave like toasts — auto-clear so they never block content (esp. on mobile).
  useEffect(() => {
    const t = setTimeout(() => setNotifs([]), 7000);
    return () => clearTimeout(t);
  }, []);

  const viewEl = useMemo(() => {
    switch (view) {
      case "cognitive": return <CognitiveCore />;
      case "continuum": return <Continuum />;
      case "gateway": return <GatewayConsole />;
      case "access": return <AccessLog />;
      case "decisions": return <DecisionCenter />;
      case "executions": return <ExecutionConsole />;
      case "integrations": return <Integrations />;
      case "risk": return <RiskView runCommand={runCommand} />;
      case "timeline": return <TimelineView />;
      case "twin": return <DigitalTwinView />;
      case "predictive": return <PredictiveView runCommand={runCommand} />;
      case "governance": return <GovernanceView />;
      case "signals": return <SignalsView insights={insights} />;
      default: return <Overview portfolio={portfolio} timeseries={timeseries} tsMeta={tsMeta} insights={insights} runCommand={runCommand} />;
    }
  }, [view, portfolio, timeseries, tsMeta, insights, runCommand]);

  return (
    <div className="nx-root fixed inset-0 flex flex-col overflow-hidden" data-testid="command-center">
      <Boot done={booted} />
      <div className="pointer-events-none absolute inset-0 nx-grid-bg opacity-40" />

      <div className="relative flex flex-1 overflow-hidden">
        <Sidebar view={view} setView={setView} />
        <MobileNav open={navOpen} setOpen={setNavOpen} view={view} setView={setView} />

        <div className="flex min-w-0 flex-1 flex-col">
          <TopBar view={view} openPalette={() => setPaletteOpen(true)} notifCount={notifs.length} onMenu={() => setNavOpen(true)} />
          <main className="nx-scroll relative flex-1 overflow-y-auto p-4 lg:p-6" data-testid="workspace">
            <AnimatePresence mode="wait">
              <motion.div
                key={view}
                initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
              >
                {viewEl}
              </motion.div>
            </AnimatePresence>
          </main>
        </div>

        <AIPanel insights={insights} loading={!insights} />
      </div>

      <Notifications items={notifs} dismiss={dismiss} />
      <CommandPalette open={paletteOpen} setOpen={setPaletteOpen} setView={setView} runCommand={runCommand} />
    </div>
  );
}
